import { useEffect, useState } from "react";
import { Panel } from "@NP-Webber/crm-kit/dist/next/index.js";
import { ScreenHeader } from "../../next/page/ScreenHeader";
import { DesignOptions } from "../../next/design/DesignOptions";
import {
  FormAlert,
  FormCard,
  FormField,
  FormGrid,
  FormSection,
} from "@NP-Webber/crm-kit";
import { shellApi, type PersonalAreaDto } from "../../api/shellApi";
import { toClientApiError } from "../../api/httpClient";

export function PersonalAreaPage() {
  const [data, setData] = useState<PersonalAreaDto | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [theme, setTheme] = useState("light");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void shellApi
      .getPersonalArea()
      .then((item) => {
        setData(item);
        setDisplayName(item.user.displayName);
        setTheme(item.preferences.theme);
      })
      .catch((err: unknown) => {
        setError(toClientApiError(err).message);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!displayName.trim() || saving) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const item = await shellApi.updatePersonalArea({
        displayName: displayName.trim(),
        theme,
        ...(newPassword ? { currentPassword, newPassword } : {}),
      });
      setData(item);
      setCurrentPassword("");
      setNewPassword("");
      setMessage("נשמר בהצלחה");
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="page-section page-section--prose" aria-label="אזור אישי">
        <FormAlert severity="info">טוען אזור אישי…</FormAlert>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="page-section page-section--prose" aria-label="אזור אישי">
        <FormAlert severity="error">{error ?? "לא ניתן לטעון את האזור האישי."}</FormAlert>
      </section>
    );
  }

  return (
    <section className="page-section page-section--prose" aria-label="אזור אישי">
      <ScreenHeader sub="הפרטים שלך, הסיסמה והמראה של המערכת" />
      {message ? <FormAlert severity="success">{message}</FormAlert> : null}

      <Panel title="מראה ותצוגה" sub="נשמר אצלך בלבד ומופעל מיד" icon="sun" tone="violet">
        <DesignOptions />
      </Panel>

      <FormCard
        title="פרופיל והעדפות"
        error={error}
        onSave={() => {
          void handleSave();
        }}
        saveLabel="שמירה"
        saving={saving}
        saveDisabled={!displayName.trim()}
      >
        <FormSection title="פרטים">
          <FormGrid cols={2}>
            <FormField
              label="שם לתצוגה"
              required
              value={displayName}
              onChange={setDisplayName}
              disabled={saving}
            />
            <FormField
              label="אימייל"
              value={data.user.email}
              onChange={() => undefined}
              disabled
            />
            <FormField
              label="ערכת נושא"
              type="select"
              value={theme}
              onChange={setTheme}
              disabled={saving}
              options={[
                { value: "light", label: "בהיר" },
                { value: "dark", label: "כהה" },
              ]}
            />
          </FormGrid>
        </FormSection>
        <FormSection title="שינוי סיסמה">
          <FormGrid cols={2}>
            <FormField
              label="סיסמה נוכחית"
              type="password"
              value={currentPassword}
              onChange={setCurrentPassword}
              disabled={saving}
              inputProps={{ autoComplete: "current-password" }}
            />
            <FormField
              label="סיסמה חדשה"
              type="password"
              value={newPassword}
              onChange={setNewPassword}
              disabled={saving}
              inputProps={{ autoComplete: "new-password" }}
              hint="השאירו ריק אם אין צורך בשינוי"
            />
          </FormGrid>
        </FormSection>
      </FormCard>
    </section>
  );
}
